import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {type MarkdownComposerRef, useMarkdownBody} from '@github-ui/commenting/useMarkdownBody'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef, useState} from 'react'

import {useCommenting} from '../../../hooks/use-commenting'

const CommitCommentEditor = ({
  ref: markdownComposerRef = null,
  initialMarkdown,
  onCancel,
  onSave,
  referenceId,
  buttonText,
  commentContent,
}: {
  commentContent?: string
  initialMarkdown?: string
  commitOid: string
  onCancel?: () => void
  onSave: (markdown: string, restMarkdownBody: () => void) => Promise<void>
  referenceId: string
  buttonText: string
  ref?: React.Ref<MarkdownComposerRef>
}) => {
  const [saving, setSaving] = useState<boolean>(false)
  const commentBoxRef = useRef<CommentBoxHandle | null>(null)
  const definedCommentContent = useRef<string | undefined>(undefined)
  const {commentBoxSubject} = useCommenting()

  const onChange = useCallback(() => {}, [])

  const {markdownBody, resetMarkdownBody, markdownValidationResult, handleMarkdownBodyChanged} = useMarkdownBody({
    commentBoxRef,
    markdownComposerRef,
    onChange,
    onCancel: () => {},
    referenceId,
    insidePortal: false,
  })

  const applyInitialMarkdown = useEffectEvent(() => {
    // Don't override the markdown saved in the session
    if (initialMarkdown && !markdownBody) {
      handleMarkdownBodyChanged(initialMarkdown)
    }
  })

  useEffect(() => {
    applyInitialMarkdown()
  }, [initialMarkdown])

  const applyCommentContent = useEffectEvent((content: string) => {
    handleMarkdownBodyChanged(content)
  })

  useEffect(() => {
    if (commentContent && definedCommentContent.current !== commentContent) {
      applyCommentContent(commentContent)
      definedCommentContent.current = commentContent
      const timeout = window.setTimeout(() => {
        if (commentBoxRef.current) {
          commentBoxRef.current?.scrollIntoView()
          commentBoxRef.current?.focus()
        }
      }, 0)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [commentContent])

  useLayoutEffect(() => {
    if (commentBoxRef && commentBoxRef.current) {
      const timeout = window.setTimeout(() => {
        commentBoxRef.current?.focus()
      }, 0)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  }, [commentBoxRef])

  const onSaveInt = async () => {
    setSaving(true)
    await onSave(markdownBody, resetMarkdownBody)
    setSaving(false)
  }

  return (
    <CommentBox
      ref={commentBoxRef}
      validationResult={markdownValidationResult}
      disabled={saving}
      onChange={newMarkdown => {
        handleMarkdownBodyChanged(newMarkdown)
      }}
      onCancel={() => {
        handleMarkdownBodyChanged('')
        onCancel?.()
      }}
      onSave={onSaveInt}
      saveButtonText={buttonText}
      saveButtonTrailingIcon={false}
      value={markdownBody}
      teamHovercardsEnabled
      fileUploadsEnabled
      subject={commentBoxSubject}
    />
  )
}

CommitCommentEditor.displayName = 'CommitCommentEditor'
export {CommitCommentEditor}
